import axios from 'axios'
import i18n from '../i18n'
import { onUnauthorized } from './authBridge'
import { onProxyInvalid } from './proxyBridge'
import { getToast } from './toastBridge'
import { getApiErrorCode, getApiErrorStatus, isCanceled } from './apiError'
import { getAccessToken } from '../auth/userManager'
import { readActingUserId } from '../auth/actingUser'
import { assertJsonResponse } from './assertJsonResponse'

// Ortak axios istemcisi. Taban adres env'den (VITE_API_URL) gelir;
// yoksa dev proxy'nin yönlendireceği '/api/v1' kullanılır.
// `||` (`??` DEĞİL) kasıtlı: docker-compose.yml'deki `${VITE_API_URL:-}` host'ta değişken tanımsızsa
// build-time'a BOŞ STRING ("") gömüyor — `??` yalnız null/undefined'ı yakalar, boş string'i "verilmiş"
// sayıp fallback'i atlar ve baseURL '' olur; tüm istekler `/api/v1` önekini kaybedip nginx'in SPA
// fallback'ine düşer (200 + index.html, axios bunu sessizce ".data" sayar). rdc dev'de 2026-08-15'te
// böyle tüm API çağrılarını bozdu (bkz. assertJsonResponse). `||` boş string'i de "yok" sayar.
const baseURL = import.meta.env.VITE_API_URL || '/api/v1'

export const apiClient = axios.create({
  baseURL,
  headers: { 'Content-Type': 'application/json' },
})

// Her istekte aktif OIDC access-token'ı yetkilendirme başlığına yazılır; token yoksa başlık eklenmez
// (korumalı uç 401 döner → interceptor login'e yönlendirir). Ayrıca sunucu lookup/satır adlarını
// Accept-Language'e göre TR/EN döndürdüğü için aktif arayüz dili gönderilir.
apiClient.interceptors.request.use(async (config) => {
  const token = await getAccessToken()
  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`)
  }
  config.headers.set('Accept-Language', i18n.language.startsWith('tr') ? 'tr' : 'en')
  // Vekil olarak çalışma (089): seçili persona her isteğe eklenir. Sunucu bunu bir İDDİA sayar ve
  // vekaletin hâlâ geçerli olduğunu her istekte kendi doğrular; geçersizse 403 PROXY_NOT_ACTIVE
  // döner (aşağıdaki response interceptor'ı kurtarma akışını tetikler). Persona seçili değilse
  // başlık HİÇ eklenmez — vekaleti olmayan kullanıcı için istek bugünküyle birebir aynı kalır.
  // Accept-Language bilinçli olarak persona'dan etkilenmez: dil, ekranı okuyan kişinin tercihidir.
  const actingUserId = readActingUserId()
  if (actingUserId != null) {
    config.headers.set('X-Acting-User-Id', String(actingUserId))
  }
  // FormData (dosya yükleme) → instance default'u olan 'application/json'ı kaldır. Aksi halde axios v1
  // FormData'yı JSON'a çevirir (parça içerikleri {} olur) ve istek multipart gitmez → sunucu
  // UNSUPPORTED_MEDIA_TYPE döner. Kaldırınca tarayıcı 'multipart/form-data; boundary=…'yı kendisi koyar.
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
    config.headers.delete('Content-Type')
  }
  return config
})

/**
 * Çapraz-kesen bir durumun ele alındığını işaretler: TanStack Query'nin global `onError` hunisi bu
 * bayrağı görünce ikinci bir jenerik toast basmaz.
 */
function markHandled(error: unknown): void {
  if (typeof error === 'object' && error !== null) {
    ;(error as { handledCrossCutting?: boolean }).handledCrossCutting = true
  }
}

/**
 * 401 → oturum sonu: deduplike yeniden-kimlik-doğrulama kancası + (yalnız tetiklenen turda) oturum-sonu
 * toast'ı.
 */
function handleUnauthorized(error: unknown): void {
  if (onUnauthorized()) {
    getToast()?.show({ severity: 'error', summary: i18n.t('common.errors.sessionExpired'), life: 4000 })
  }
  markHandled(error)
}

/**
 * 403 PROXY_NOT_ACTIVE → vekalet çalışırken geçersizleşti (089). 401 ile aynı desen: tek noktada,
 * deduplike bir kurtarma tetiklenir (çerezi sil → önbelleği geçersiz kıl → anasayfa). Kullanıcı "yetkiniz
 * yok" yerine ne olduğunu anlatan tek bir mesaj görür.
 */
function handleProxyInvalid(error: unknown): void {
  if (onProxyInvalid()) {
    getToast()?.show({ severity: 'warn', summary: i18n.t('proxy.revoked'), life: 6000 })
  }
  markHandled(error)
}

/** Vekalet geçersizliği mi? (403 + koda göre; başka bir 403 ile karıştırılmaz.) */
function isProxyInvalid(error: unknown): boolean {
  return getApiErrorStatus(error) === 403 && getApiErrorCode(error) === 'PROXY_NOT_ACTIVE'
}

// Merkezi hata yönetimi (019): çapraz-kesen durumları burada ele al; genel toast'lama TanStack Query
// global onError → notifyApiError'a bırakılır (tek toast noktası). Interceptor yalnız iptali sessizce
// geçirir ve çapraz-kesen iki durumu (oturum sonu / geçersiz vekalet) kendi yardımcılarına devreder —
// dallar buraya gömülsün diye değil: ölçüldüğünde tek gövde bilişsel karmaşıklıkta 14'e çıkıyordu
// (Sonar S3776 eşiği 15) ve bir sonraki dal onu aşardı.
apiClient.interceptors.response.use(
  (response) => {
    assertJsonResponse(response)
    return response
  },
  (error: unknown) => {
    // İptal edilen istek → hiçbir bildirim (FR-009).
    if (isCanceled(error)) return Promise.reject(error)

    if (getApiErrorStatus(error) === 401) {
      handleUnauthorized(error)
    } else if (isProxyInvalid(error)) {
      handleProxyInvalid(error)
    }

    // Diğer durumlar olduğu gibi reddedilir → RQ global onError sınıflandırır ve toast'lar.
    return Promise.reject(error)
  },
)
